import { ChevronDown } from 'lucide-react';
import { DevToolsCopyBox } from 'Modules/softphone/components/SoftphoneDevTools/DevToolsCopyBox';
import { DevToolsDirectionBadge } from 'Modules/softphone/components/SoftphoneDevTools/DevToolsDirectionBadge';
import { DevToolsMicroLabel } from 'Modules/softphone/components/SoftphoneDevTools/DevToolsMicroLabel';
import { DevToolsStatusBadge } from 'Modules/softphone/components/SoftphoneDevTools/DevToolsStatusBadge';
import { CallsAudioDevices } from 'Modules/softphone/components/SoftphoneDevTools/DevToolsTabs/CallsTab/CallsAudioDevices';
import type { CallsCardProps } from 'Modules/softphone/components/SoftphoneDevTools/DevToolsTabs/CallsTab/CallsCard/index.types';
import { CallsMediaStrip } from 'Modules/softphone/components/SoftphoneDevTools/DevToolsTabs/CallsTab/CallsMediaStrip';
import { CallsStateBadge } from 'Modules/softphone/components/SoftphoneDevTools/DevToolsTabs/CallsTab/CallsStateBadge';
import {
  DIRECTION,
  SURFACE,
  TONE,
} from 'Modules/softphone/components/SoftphoneDevTools/styles';
import {
  formatTimestamp,
  isSipIdentifier,
} from 'Modules/softphone/components/SoftphoneDevTools/utils';
import { useCallDuration } from 'Modules/softphone/components/SoftphoneHeldCalls/hooks/useCallDuration';
import { useSoftphoneContact } from 'Modules/softphone/hooks/useSoftphoneContact';
import React, { useState } from 'react';
import { formatDuration } from 'Utils/formatDuration';
import { cn } from '~/lib/utils';

/**
 * CallsCard - Everything known about one call leg
 *
 * Dense on purpose.
 */
export const CallsCard: React.FC<CallsCardProps> = ({ call, isCurrentCall }) => {
  const { phoneNumber: resolvedPhoneNumber } = useSoftphoneContact(call.id);
  const [showDevices, setShowDevices] = useState(false);

  const duration = useCallDuration(
    call.answerTime ?? call.startTime,
    call.duration
  );

  const sipIdentifier = call.phoneNumber;
  const showSipIdentifier = isSipIdentifier(sipIdentifier);
  const hasResolvedPhone =
    resolvedPhoneNumber && resolvedPhoneNumber !== sipIdentifier;

  const stripe = DIRECTION[call.direction].stripe;
  const holdTone = call.isOnHold ? TONE.warn : TONE.ok;

  return (
    <div
      className={cn(
        SURFACE.card,
        'border-l-[3px]',
        stripe,
        isCurrentCall && 'ring-1 ring-inset ring-emerald-800'
      )}
    >
      <div className="flex items-center gap-[9px] px-3 py-[11px]">
        <DevToolsDirectionBadge direction={call.direction} compact />

        {/* The destination is not repeated here: it is printed once below,
            under the label that says whether it is a number or a SIP id. */}
        <div className="min-w-0 flex-1 truncate text-[13px] font-semibold text-zinc-200">
          {call.displayName ?? call.phoneNumber}
        </div>

        {isCurrentCall && (
          <DevToolsStatusBadge label="Current" variant="primary" />
        )}

        <div className="flex-shrink-0 text-right">
          <div className="font-sp-mono text-[15px] font-semibold text-zinc-200">
            {formatDuration(duration)}
          </div>
          <div
            className={cn(
              'mt-px flex items-center justify-end gap-1 text-[10px] font-semibold uppercase tracking-[0.05em]',
              holdTone.text
            )}
          >
            <span
              className={cn('h-[5px] w-[5px] rounded-full', holdTone.dot)}
            />
            {call.isOnHold ? 'On Hold' : call.state}
          </div>
        </div>
      </div>

      <CallsMediaStrip callId={call.id} isMuted={call.isMuted} />

      <div className="space-y-2.5 border-t border-zinc-800 p-3">
        <div className="grid grid-cols-2 gap-3">
          <div className="min-w-0">
            <DevToolsMicroLabel>
              {showSipIdentifier ? 'SIP Identifier' : 'Phone Number'}
            </DevToolsMicroLabel>
            <div className="mt-0.5 truncate font-sp-mono text-[12.5px] text-zinc-200">
              {call.phoneNumber}
            </div>
          </div>

          {showSipIdentifier && hasResolvedPhone && (
            <div className="min-w-0">
              <DevToolsMicroLabel>Phone Number</DevToolsMicroLabel>
              <div className="mt-0.5 truncate font-sp-mono text-[12.5px] text-zinc-200">
                {resolvedPhoneNumber}
              </div>
            </div>
          )}

          <div className="min-w-0">
            <DevToolsMicroLabel>Started</DevToolsMicroLabel>
            <div className="mt-0.5 truncate font-sp-mono text-[12.5px] text-zinc-200">
              {formatTimestamp(call.startTime, { fallback: '—' })}
            </div>
          </div>

          {call.personId && (
            <div className="min-w-0">
              <DevToolsMicroLabel>Person ID</DevToolsMicroLabel>
              <div className="mt-0.5 truncate font-sp-mono text-[12.5px] text-zinc-200">
                {call.personId}
              </div>
            </div>
          )}

          <div className="min-w-0">
            <DevToolsMicroLabel>State</DevToolsMicroLabel>
            <div className="mt-0.5">
              <CallsStateBadge state={call.state} />
            </div>
          </div>
        </div>

        <DevToolsCopyBox value={call.id} label="Call ID" />
      </div>

      <div className="border-t border-zinc-800">
        <button
          type="button"
          onClick={() => setShowDevices((open) => !open)}
          aria-expanded={showDevices}
          className="flex w-full items-center gap-1.5 px-3 py-2 text-[11.5px] font-medium text-emerald-400 transition-colors hover:bg-zinc-800 hover:text-emerald-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-emerald-400/40"
        >
          <ChevronDown
            className={cn(
              'h-3 w-3 transition-transform',
              showDevices && 'rotate-180'
            )}
          />
          Audio devices
        </button>
        {showDevices && <CallsAudioDevices />}
      </div>
    </div>
  );
};
